import React, { useState, useEffect } from 'react';
import { DocumentNotesApi } from '../../services/api/DocumentNotesApi';
import { DocumentApi } from '../../services/api/DocumentApi';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext'; // ✅ NEW: Import DataContext for notes caching
import { format } from 'date-fns';
import './CourseNotesList.css';

/**
 * Helper: Strip markdown and HTML from content
 */
const stripMd = (s) =>
  (s || "")
    .replace(/```[\s\S]*?```/g, " ")      // code blocks
    .replace(/`[^`]*`/g, " ")            // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")// images
    .replace(/\[[^\]]*\]\([^)]*\)/g, " ") // links -> text removed
    .replace(/<[^>]*>/g, " ")           // HTML tags
    .replace(/[#>*_\-|]/g, " ")          // basic md tokens
    .replace(/\s+/g, " ")
    .trim();

/**
 * Helper: Get preview line from markdown content
 */
const getPreviewLine = (raw) => {
  const s = stripMd(raw);
  if (!s) return "";
  return s.length > 200 ? s.slice(0, 200) + "…" : s;
};

/**
 * Helper: Format relative time
 */
const formatRelative = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return format(date, 'MMM d');
};

/**
 * Helper: Format file size
 */
const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 KB';
  const kb = bytes / 1024;
  return kb < 1 ? '0.1 KB' : `${kb.toFixed(1)} KB`;
};

/**
 * CourseNotesList Component
 * Displays list of notes for a specific course with create/edit functionality
 * 
 * @param {Object} props
 * @param {number} props.endeavorId - Course/endeavor ID
 * @param {string} props.courseName - Course name
 * @param {Function} props.onSelectNote - Callback when note is selected for editing
 */
const CourseNotesList = ({ endeavorId, courseName, onSelectNote }) => {
  const { user } = useAuth();
  const { fetchNotesByEndeavor, fetchNoteContent, invalidateNotesCache } = useData(); // ✅ Use DataContext for notes caching
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('updatedDesc');
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [savingTitleId, setSavingTitleId] = useState(null);

  // Load notes for this course
  useEffect(() => {
    const loadNotes = async () => {
      if (!endeavorId || !user?.userId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch metadata list
        const notesList = await fetchNotesByEndeavor(endeavorId);
        setNotes(notesList);

        // Fetch content previews in parallel (non-blocking — updates rows as they resolve)
        if (notesList.length > 0) {
          notesList.forEach(async (note) => {
            try {
              const content = await fetchNoteContent(note.id);
              if (content) {
                setNotes(prev => prev.map(n =>
                  n.id === note.id ? { ...n, content } : n
                ));
              }
            } catch (e) {
              // Silently skip — preview falls back to description
              console.debug('[CourseNotesList] Preview fetch skipped for note:', note.id);
            }
          });
        }
      } catch (err) {
        console.error('[CourseNotesList] Failed to load notes:', err);
        setError('Failed to load notes. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadNotes();

    // Listen for document changes to refresh list
    const handleDocumentChange = () => {
      // ✅ Invalidate cache and reload
      invalidateNotesCache(endeavorId);
      loadNotes();
    };

    window.addEventListener('documents:changed', handleDocumentChange);

    return () => {
      window.removeEventListener('documents:changed', handleDocumentChange);
    };
  }, [endeavorId, user?.userId, fetchNotesByEndeavor, fetchNoteContent, invalidateNotesCache]);

  // Create note and open it directly in the rich editor
  const handleCreateNote = async () => {
    if (!endeavorId) return;
    try {
      const now = new Date();
      const timestamp = format(now, 'MM/dd/yyyy HH:mm');
      const noteTitle = `${courseName} Note ${timestamp}`;
      const noteData = {
        title: noteTitle,
        description: `Course note created on ${timestamp}`,
        content: '',
        endeavorId
      };
      const created = await DocumentNotesApi.createNote(noteData);
      invalidateNotesCache(endeavorId);
      // Open the new note directly in the full NoteEditor
      if (onSelectNote) {
        onSelectNote({ id: created.id, name: noteTitle, content: '' });
      }
    } catch (err) {
      console.error('Failed to create note:', err);
      setError('Failed to create note. Please try again.');
    }
  };

  // Open note - fetch content on-demand when clicked
  const openNote = async (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note || !onSelectNote) return;
    
    try {
      // Fetch content only when note is opened (lazy loading)
      const content = await fetchNoteContent(noteId);
      const noteWithContent = { ...note, content };
      onSelectNote(noteWithContent);
    } catch (err) {
      console.error('[CourseNotesList] Failed to load note content:', err);
      // Pass note without content if fetch fails
      onSelectNote({ ...note, content: '' });
    }
  };

  // Start rename
  const startRename = (note) => {
    setEditingTitleId(note.id);
    setDraftTitle(note.name || note.title || 'Untitled note');
  };

  // Commit rename
  const commitRename = async (note) => {
    const newName = (draftTitle || '').trim();
    const oldName = note.name || note.title || 'Untitled note';
    if (!newName || newName === oldName) {
      setEditingTitleId(null);
      setDraftTitle('');
      return;
    }

    // Optimistic UI update
    setNotes((prev) =>
      prev.map((n) => (n.id === note.id ? { ...n, name: newName, title: newName } : n))
    );

    setSavingTitleId(note.id);
    try {
      await DocumentNotesApi.renameNote(note.id, newName);
      
      // Invalidate cache so next load picks up new name
      invalidateNotesCache(endeavorId);
      setError(null);
      
      // Notify other components that documents have changed
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (e) {
      // Revert on failure
      console.error('[CourseNotesList] Rename failed:', e);
      setNotes((prev) =>
        prev.map((n) => (n.id === note.id ? { ...n, name: oldName, title: oldName } : n))
      );
      
      // Set user-friendly error message
      if (e.message.includes('404')) {
        setError('Note not found. It may have been deleted.');
      } else if (e.message.includes('403')) {
        setError('You do not have permission to rename this note.');
      } else if (e.message.includes('400')) {
        setError('Invalid note name. Please try a different name.');
      } else {
        setError('Failed to rename note. Please check your connection and try again.');
      }
    } finally {
      setSavingTitleId(null);
      setEditingTitleId(null);
      setDraftTitle('');
    }
  };

  // Cancel rename
  const cancelRename = () => {
    setEditingTitleId(null);
    setDraftTitle('');
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      await DocumentApi.deleteDocument(noteId);
      
      // ✅ NEW: Invalidate cache before updating local state
      invalidateNotesCache(endeavorId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (e) {
      console.error('Delete failed', e);
      setError('Failed to delete note. Please try again.');
    }
  };

  // Filter and sort notes
  const filteredNotes = notes
    .filter(note => {
      if (!query) return true;
      const searchLower = query.toLowerCase();
      return (note.name || '').toLowerCase().includes(searchLower) ||
             (note.content || '').toLowerCase().includes(searchLower);
    })
    .sort((a, b) => {
      if (sort === 'updatedDesc') {
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      } else if (sort === 'createdDesc') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sort === 'titleAsc') {
        return (a.name || '').localeCompare(b.name || '');
      }
      return 0;
    });

  if (loading) {
    return (
      <div className="course-notes-list course-notes-list--loading">
        <div className="spinner"></div>
        <p>Loading notes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="course-notes-list course-notes-list--error">
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (notes.length === 0 && !loading) {
    return (
      <div className="course-notes-list">
        <div className="notes-empty-state">
          <div className="notes-empty-illustration">
            <div className="notes-empty-spotlight" />
            <img src="/scout_images/Scout_Thumbsup.png" alt="Scout" className="notes-empty-scout" />
          </div>

          <h2 className="notes-empty-title">Your notebook starts here</h2>

          <p className="notes-empty-description">
            Capture lecture notes, ideas, and reminders for this course.
            Keep everything organized so reviewing later is easier.
          </p>

          <button className="notes-empty-cta" onClick={handleCreateNote}>
            Create Your First Note
          </button>

          <div className="notes-empty-features">
            <div className="notes-empty-feature">
              <svg className="notes-feature-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              <span>Write lecture notes</span>
            </div>
            <div className="notes-empty-feature">
              <svg className="notes-feature-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
              <span>Capture important ideas</span>
            </div>
            <div className="notes-empty-feature">
              <svg className="notes-feature-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
              <span>Save things to remember</span>
            </div>
            <div className="notes-empty-feature">
              <svg className="notes-feature-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <span>Quickly review later</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Notes list
  return (
    <div className="course-notes-list">
      <div className="siq-notes-header">
        <h2 className="siq-notes-title">Course Notes</h2>
        <span className="siq-notes-count">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="siq-notes-toolbar">
        <input
          className="siq-notes-search"
          placeholder="Search notes…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="siq-notes-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="updatedDesc">Sort: Most recent</option>
          <option value="createdDesc">Sort: Newest</option>
          <option value="titleAsc">Sort: Title A–Z</option>
        </select>
        <button className="siq-notes-new-btn" onClick={handleCreateNote}>+ New Note</button>
      </div>

      <div className="siq-notes-list-container">
        {filteredNotes.length === 0 ? (
          <div className="siq-notes-empty" style={{ minHeight: 200 }}>
            <div className="siq-empty-title">No matching notes</div>
            <div className="siq-empty-sub">Try a different search query.</div>
          </div>
        ) : (
          filteredNotes.map(note => (
            <div
              key={note.id}
              className="siq-note-row"
              role="button"
              tabIndex={0}
              onClick={() => openNote(note.id)}
              onKeyDown={(e) => e.key === 'Enter' && openNote(note.id)}
            >
              <div className="siq-note-row-top">
                {editingTitleId === note.id ? (
                  <input
                    className="siq-note-title-input"
                    value={draftTitle}
                    autoFocus
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(note);
                      if (e.key === 'Escape') cancelRename();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="siq-note-title">
                    {note.name || note.title || 'Untitled note'}
                    {savingTitleId === note.id && <span className="siq-title-saving">Saving…</span>}
                  </div>
                )}

                <div className="siq-note-actions">
                  {editingTitleId === note.id ? (
                    <>
                      <button className="siq-action-btn siq-confirm-btn" onClick={(e) => { e.stopPropagation(); commitRename(note); }} aria-label="Save title" type="button" title="Save">✓</button>
                      <button className="siq-action-btn siq-cancel-btn" onClick={(e) => { e.stopPropagation(); cancelRename(); }} aria-label="Cancel" type="button" title="Cancel">✕</button>
                    </>
                  ) : (
                    <>
                      <button className="siq-action-btn siq-rename-btn" onClick={(e) => { e.stopPropagation(); startRename(note); }} aria-label="Rename note" type="button" title="Rename">✏️</button>
                      <button className="siq-action-btn siq-delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id); }} aria-label="Delete note" type="button" title="Delete">🗑️</button>
                    </>
                  )}
                </div>
              </div>

              <div className="siq-note-meta">
                <span>{formatRelative(note.updatedAt || note.createdAt)}</span>
                <span className="siq-meta-dot">•</span>
                <span>{formatSize(note.fileSize)}</span>
              </div>

              <div className="siq-note-preview">
                {note.content ? getPreviewLine(note.content) : (note.description || 'No content yet')}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CourseNotesList;
